import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class LoadingService {
    private loadingSubject = new BehaviorSubject<boolean>(false);
    private savingSubject = new BehaviorSubject<boolean>(false);
    private blockPageSubject = new BehaviorSubject<boolean>(false);

    private loadingTimeout: ReturnType<typeof setTimeout> | null = null;
    private savingTimeout: ReturnType<typeof setTimeout> | null = null;

    private readonly LOADING_DELAY = 500;
    private readonly SAVING_DELAY = 500;

    private loadingCounter = 0;
    private savingCounter = 0;

    readonly loading$ = this.loadingSubject.asObservable();
    readonly saving$ = this.savingSubject.asObservable();
    readonly blockPage$ = this.blockPageSubject.asObservable();

    loadingOn(): void {
        this.loadingCounter++;
        this.scheduleLoadingShow();
    }

    loadingOff(): void {
        this.loadingCounter = Math.max(0, this.loadingCounter - 1);

        if (this.loadingCounter === 0) {
            this.clearLoadingTimeout();
            this.loadingSubject.next(false);
        }
    }

    savingOn(blockPage = false): void {
        this.savingCounter++;
        this.scheduleSavingShow(blockPage);
    }

    savingOff(): void {
        this.savingCounter = Math.max(0, this.savingCounter - 1);

        if (this.savingCounter === 0) {
            this.clearSavingTimeout();
            this.savingSubject.next(false);
            this.blockPageSubject.next(false);
        }
    }

    blockPageOn(): void {
        this.blockPageSubject.next(true);
    }

    blockPageOff(): void {
        this.blockPageSubject.next(false);
    }

    resetLoading(): void {
        this.loadingCounter = 0;
        this.clearLoadingTimeout();
        this.loadingSubject.next(false);
    }

    resetSaving(): void {
        this.savingCounter = 0;
        this.clearSavingTimeout();
        this.savingSubject.next(false);
        this.blockPageSubject.next(false); // Fixed: Reset blockPage state
    }

    resetAll(): void {
        this.resetLoading();
        this.resetSaving();
    }

    private scheduleLoadingShow(): void {
        // Only schedule if not already scheduled and not currently showing
        if (!this.loadingTimeout && !this.loadingSubject.value) {
            this.loadingTimeout = setTimeout(() => {
                // Double-check counter is still > 0 when timeout executes
                if (this.loadingCounter > 0) {
                    this.loadingSubject.next(true);
                }
                this.loadingTimeout = null;
            }, this.LOADING_DELAY);
        }
    }

    private scheduleSavingShow(blockPage: boolean): void {
        // Only schedule if not already scheduled and not currently showing
        if (!this.savingTimeout && !this.savingSubject.value) {
            this.savingTimeout = setTimeout(() => {
                this.blockPageSubject.next(blockPage);
                // Double-check counter is still > 0 when timeout executes
                if (this.savingCounter > 0) {
                    this.savingSubject.next(true);
                }
                this.savingTimeout = null;
            }, this.SAVING_DELAY);
        }
    }

    private clearLoadingTimeout(): void {
        if (this.loadingTimeout) {
            clearTimeout(this.loadingTimeout);
            this.loadingTimeout = null;
        }
    }

    private clearSavingTimeout(): void {
        if (this.savingTimeout) {
            clearTimeout(this.savingTimeout);
            this.savingTimeout = null;
        }
    }
}
